// "Deliver later" presets + formatting. Pure date math, no UI.

/** A preset with its label + time resolved together, so they can't disagree. */
export type ResolvedPreset = {
  id: string;
  label: string;
  icon: string;
  when: Date;
};

function at(hour: number, dayOffset: number): Date {
  const d = new Date();
  d.setDate(d.getDate() + dayOffset);
  d.setHours(hour, 0, 0, 0);
  return d;
}

function nextWeekday(targetDow: number, hour: number): Date {
  const d = new Date();
  d.setHours(hour, 0, 0, 0);
  let add = (targetDow - d.getDay() + 7) % 7;
  if (add === 0 && d.getTime() <= Date.now()) add = 7;
  d.setDate(d.getDate() + add);
  return d;
}

/**
 * Build the deliver-later presets fresh, with labels chosen to match the
 * actual time:
 *   - the evening slot says "This evening" only while tonight's 8pm is
 *     still ahead; past that it becomes "Tomorrow evening" (8pm next day),
 *     so the words never contradict the time.
 *   - "Tomorrow morning" is always tomorrow 9am.
 *   - "This weekend" is the next Saturday 9am.
 * Then we de-dupe by resolved minute (on a Friday, "This weekend" lands on
 * the same Saturday-9am as "Tomorrow morning", so the redundant one drops).
 */
export function buildPresets(): ResolvedPreset[] {
  const now = Date.now();
  const tonight = at(20, 0);
  const eveningAhead = tonight.getTime() > now;

  const raw: ResolvedPreset[] = [
    eveningAhead
      ? { id: 'evening', icon: '🌙', label: 'This evening', when: tonight }
      : { id: 'evening', icon: '🌙', label: 'Tomorrow evening', when: at(20, 1) },
    { id: 'morning', icon: '☀️', label: 'Tomorrow morning', when: at(9, 1) },
    { id: 'weekend', icon: '🗓️', label: 'This weekend', when: nextWeekday(6, 9) },
  ];

  const seen = new Set<number>();
  const out: ResolvedPreset[] = [];
  for (const p of raw) {
    const key = Math.round(p.when.getTime() / 60000);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(p);
  }
  return out;
}

/**
 * Friendly delivery label, e.g. "tomorrow 09:00", "this evening 20:00",
 * "Sat 09:00", or "May 30, 14:30" for further-out dates.
 */
export function formatDelivery(d: Date): string {
  const now = new Date();
  const time = d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });

  const startOfDay = (x: Date) => {
    const c = new Date(x);
    c.setHours(0, 0, 0, 0);
    return c.getTime();
  };
  const dayDiff = Math.round((startOfDay(d) - startOfDay(now)) / 86_400_000);

  if (dayDiff === 0) return `today ${time}`;
  if (dayDiff === 1) return `tomorrow ${time}`;
  if (dayDiff > 1 && dayDiff < 7) {
    const wd = d.toLocaleDateString(undefined, { weekday: 'short' });
    return `${wd} ${time}`;
  }
  const date = d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  return `${date}, ${time}`;
}

/** For the <input type="datetime-local"> value (local time, no seconds). */
export function toDatetimeLocalValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(
    d.getHours(),
  )}:${pad(d.getMinutes())}`;
}
